import Box from '@mui/material/Box';
import { ThemeColors } from 'core/utilities/theme';
import React, { createRef, useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import isEqual from 'react-fast-compare';
import type { EntitySequence as Sequence, SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { SequenceSelection } from 'sequenceAnalyser/utilities/selection/selection';
import type { FeatureDefinitions } from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import { buildFeatureId } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

export interface ActiveViewPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ComponentProps {
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  featureDefinitions: FeatureDefinitions;
  width: number;
  height: number;
  activeViewPosition: ActiveViewPosition;
  onActiveViewPositionChange: (newActivePositionChange: ActiveViewPosition) => void;
  selection: SequenceSelection;
}

type CanvasRenderedData = [number, number, Sequence[], SequenceAlign[], FeatureDefinitions, SequenceSelection];

function SequencesNavigator({
  width, height, sequences, alignedSequences, featureDefinitions, activeViewPosition, onActiveViewPositionChange,
  selection,
}: ComponentProps) {
  const [
    lastCanvasRenderedData, setLastCanvasRenderedData,
  ] = useState<CanvasRenderedData | undefined>();

  const canvasRef = createRef<HTMLCanvasElement>();

  useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      const canvasRenderedData: CanvasRenderedData = [
        width, height, sequences, alignedSequences, featureDefinitions, selection,
      ];

      if (ctx && !isEqual(lastCanvasRenderedData, canvasRenderedData)) {
        const maxResiduesLength = alignedSequences[0].residues.length;
        const residueWidth = width / maxResiduesLength;
        const lineHeight = height / alignedSequences.length;

        ctx.clearRect(0, 0, width, height);

        // Set a light grey background meaning there are residues everywhere.
        // Then iterate below over sequences and add white background for gaps.
        ctx.fillStyle = ThemeColors.sequenceAnalyserNavigatorResidue;

        ctx.fillRect(
          0,
          0,
          width,
          height,
        );

        alignedSequences.forEach((alignedSequence, i) => {
          const sequence = sequences.find((s) => s.id === alignedSequence.id) as Sequence;

          // Iterate over residues to create canvas rectangle only when there is a gap
          alignedSequence.residues.split('').forEach((r, rIndex) => {
            if (r !== '.') return;

            ctx.fillStyle = ThemeColors.sequenceAnalyserNavigatorGap;

            ctx.fillRect(
              rIndex * residueWidth,
              i * lineHeight,
              residueWidth,
              lineHeight,
            );
          });

          // Iterate over features to create canvas rectangle representing them
          sequence.features.forEach((f) => {
            if (!featureDefinitions[buildFeatureId(f)].visible) return;

            ctx.fillStyle = `rgb(${featureDefinitions[buildFeatureId(f)].color.join(',')})`;

            ctx.fillRect(
              f.start * residueWidth,
              i * lineHeight,
              (f.stop - f.start + 1) * residueWidth,
              lineHeight,
            );
          });

          // Create canvas rectangle representing the selection (over everything else)
          if (selection.ids.includes(sequence.id)) {
            ctx.fillStyle = ThemeColors.dataAnalyzerSelection;

            if (selection.positionStart !== undefined && selection.positionEnd !== undefined) {
              // Highlight position
              ctx.fillRect(
                selection.positionStart * residueWidth,
                i * lineHeight,
                (selection.positionEnd + 1 - selection.positionStart) * residueWidth,
                lineHeight,
              );
            } else {
              // Highlight whole line
              ctx.fillRect(
                0,
                i * lineHeight,
                alignedSequence.residues.length * residueWidth,
                lineHeight,
              );
            }
          }
        });

        // Draw a border around the selection yellow rectangle
        if (selection.positionStart !== undefined && selection.positionEnd !== undefined) {
          ctx.strokeStyle = ThemeColors.textColor;
          ctx.lineWidth = 2;
          ctx.strokeRect(
            selection.positionStart * residueWidth,
            alignedSequences.findIndex((s) => selection.ids.includes(s.id)) * lineHeight,
            (selection.positionEnd + 1 - selection.positionStart) * residueWidth,
            selection.ids.length * lineHeight,
          );
        }

        setLastCanvasRenderedData(canvasRenderedData);
      }
    }
  }, [sequences, alignedSequences, canvasRef, height, width, featureDefinitions, lastCanvasRenderedData, selection]);

  useEffect(() => () => setLastCanvasRenderedData(undefined), []);

  const handleX = activeViewPosition.x + activeViewPosition.width > width
    ? width - activeViewPosition.width
    : activeViewPosition.x;
  const handleY = activeViewPosition.y + activeViewPosition.height > height
    ? height - activeViewPosition.height
    : activeViewPosition.y;

  return (
    <Box sx={{
      position: 'relative',
      width,
      height,
      overflow: 'hidden',
      background: 'white',
    }}
    >
      <Draggable
        axis="both"
        handle=".activeViewPositionHandle"
        bounds="parent"
        position={{ x: handleX, y: handleY }}
        positionOffset={undefined}
        onStop={(e, draggableData) => {
          onActiveViewPositionChange({
            ...activeViewPosition,
            x: draggableData.x,
            y: draggableData.y,
          });
        }}
      >
        <Box
          className="activeViewPositionHandle"
          sx={{
            position: 'absolute',
            width: `${activeViewPosition.width}px`,
            height: `${activeViewPosition.height}px`,
            boxShadow: 'inset 0 0 0 2px red',
            outline: '1px solid white',
            cursor: 'move',
            backgroundColor: 'rgba(255,255,255, 0.25)',
          }}
        />
      </Draggable>
      <canvas ref={canvasRef} width={width} height={height} />
    </Box>
  );
}
export default SequencesNavigator;
